import React, { useEffect, useMemo, useState, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import { clearanceTypes } from '../../data/mockData';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TableEmptyRow from '../../components/TableEmptyRow';
import StatusBadge from '../../components/StatusBadge';
import { printTableElement } from '../../utils/exportUtils';
import { clearancesAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';

const CLEARANCE_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under-review',
  Approved: 'approved',
  Rejected: 'rejected',
  Expired: 'critical',
};

const Clearance = () => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const showProjectColumn = projectFilter === 'all';
  const tableRef = useRef(null);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [clearances, setClearances] = useState([]);
  const [useApiData, setUseApiData] = useState(false);
  const [newItem, setNewItem] = useState({
    project_id: '',
    clearance_type: '',
    authority: '',
    submission_date: '',
    target_date: '',
    status: 'Submitted',
    remarks: '',
    attachments: [],
  });
  const [formError, setFormError] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  useEffect(() => {
    const loadClearances = async () => {
      try {
        const response = await clearancesAPI.getAll({ page: 1, pageSize: 100, skipCount: '1' });
        const { rows: rawRows } = unwrapListResponse(response);
        const rows = safeArray(rawRows).map((row) => {
          const payload = row.data || {};
          return {
            id: row.id,
            project_id: row.projectId ?? row.project_id ?? 1,
            clearance_ref: payload.clearance_ref ?? row.clearance_ref ?? `CLR-${row.id}`,
            clearance_type: row.title || payload.clearance_type || row.clearance_type || '',
            authority: payload.authority ?? row.authority ?? '',
            submission_date: payload.submission_date ?? row.submission_date ?? '',
            target_date: payload.target_date ?? row.target_date ?? '',
            status: row.status || payload.status || 'Submitted',
            remarks: payload.remarks ?? row.remarks ?? '',
            linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
          };
        });
        setClearances(rows);
        setUseApiData(true);
      } catch (error) {
        console.warn('Clearances API unavailable, using local data:', error?.message || error);
        setClearances([]);
        setUseApiData(false);
      } finally {
        setDataReady(true);
      }
    };
    loadClearances();
  }, []);

  const projects = safeArray(projectOptions);

  const filteredItems = useMemo(() => {
    let items = [...safeArray(clearances)];
    if (projectFilter !== 'all') items = items.filter(i => i.project_id === parseInt(projectFilter, 10));
    if (statusFilter !== 'all') items = items.filter(i => i.status === statusFilter);
    return items;
  }, [clearances, projectFilter, statusFilter]);

  const getStatusClass = (status) => CLEARANCE_STATUS_CLASS_MAP[status] || 'draft';

  const handleSave = async () => {
    if (!newItem.project_id || !newItem.clearance_type.trim() || !newItem.authority.trim() || !newItem.submission_date || !newItem.target_date) {
      setFormError('Project, clearance type, authority, submission date, and target date are required.');
      return;
    }
    if (new Date(newItem.target_date) < new Date(newItem.submission_date)) {
      setFormError('Target date cannot be before submission date.');
      return;
    }
    if (editingId) {
      if (!(await confirmDialog('Save changes to this clearance?', 'Edit Clearance'))) return;
    } else {
      if (!(await confirmDialog('Create this clearance?', 'New Clearance'))) return;
    }

    if (useApiData) {
      const createViaApi = async () => {
        try {
          const projectId = parseInt(newItem.project_id, 10);
          const project = projects.find((p) => p.id === projectId);
          const payload = {
            projectId,
            title: newItem.clearance_type.trim(),
            status: newItem.status,
            data: {
              clearance_ref: `${project?.agreement_prefix || 'GEN'}/CLR/${Date.now().toString().slice(-6)}`,
              clearance_type: newItem.clearance_type.trim(),
              authority: newItem.authority.trim(),
              submission_date: newItem.submission_date,
              target_date: newItem.target_date,
              remarks: newItem.remarks.trim(),
              attachments: newItem.attachments || [],
              linked_correspondence: null,
            },
          };
          const response = editingId
            ? await clearancesAPI.update(editingId, payload)
            : await clearancesAPI.create(payload);
          const created = response?.data;
          const data = created?.data || {};
          const mapped = {
            id: created.id,
            project_id: created.projectId ?? projectId,
            clearance_ref: data.clearance_ref || `${project?.agreement_prefix || 'GEN'}/CLR/${created.id}`,
            clearance_type: created.title || data.clearance_type || '',
            authority: data.authority || '',
            submission_date: data.submission_date || '',
            target_date: data.target_date || '',
            status: created.status || 'Submitted',
            remarks: data.remarks || '',
            attachments: Array.isArray(data.attachments) ? data.attachments : [],
            linked_correspondence: data.linked_correspondence || null,
          };
          setClearances((prev) =>
            editingId ? prev.map((c) => (c.id === editingId ? mapped : c)) : [mapped, ...prev],
          );
          setFormError('');
          setShowModal(false);
          setEditingId(null);
          setNewItem({
            project_id: '',
            clearance_type: '',
            authority: '',
            submission_date: '',
            target_date: '',
            status: 'Submitted',
            remarks: '',
            attachments: [],
          });
          notifySuccess(editingId ? 'Clearance updated successfully.' : 'Clearance created successfully.');
        } catch (error) {
          console.error('Clearance create failed via API:', error);
          setFormError('Failed to create clearance via API.');
          notifyError('Failed to create clearance. Please retry.');
        }
      };
      createViaApi();
      return;
    }

    setFormError('API unavailable. Please connect to the backend to create or update clearances.');
    notifyError('API unavailable. Please connect to the backend.');
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewItem((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setNewItem((prev) => {
      const next = [...(prev.attachments || [])];
      next.splice(index, 1);
      return { ...prev, attachments: next };
    });
  };

  const handleDownloadClearanceAttachment = (item, file) => {
    const fallbackText = [
      'Clearance Attachment',
      `Clearance Ref: ${item.clearance_ref}`,
      `Type: ${item.clearance_type}`,
      `File Name: ${file.name}`,
      '',
      'Metadata-only file record.',
    ].join('\n');
    downloadAttachment(file, fallbackText);
  };

  const handleViewClearanceAttachment = (item, file) => {
    viewAttachmentInNewTab(file, () => handleDownloadClearanceAttachment(item, file));
  };

  return (
    <div>
      <div className="section-header">
        <h2>Clearance Register <span className="count-badge">{dataReady ? filteredItems.length : '…'}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Clearance Register - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Clearance
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Under Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Expired">Expired</option>
        </select>
        <ResetFiltersButton onClick={() => { setProjectFilter('all'); setStatusFilter('all'); }} />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Clearance Ref</th>
              {showProjectColumn && <th>Project</th>}
              <th>Type</th>
              <th>Authority</th>
              <th>Submission Date</th>
              <th>Target Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!dataReady ? (
              <tr>
                <td colSpan={showProjectColumn ? 7 : 6} style={{ textAlign: 'center', padding: 32, color: '#6b7280', fontSize: 13 }}>
                  Loading clearances…
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <TableEmptyRow colSpan={showProjectColumn ? 7 : 6} message="No clearances found." />
            ) : (
              filteredItems.map((item) => {
                const project = projects.find(p => p.id === item.project_id);
                return (
                  <tr key={item.id} className="clickable" onClick={() => setSelectedItem(item)}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{item.clearance_ref}</td>
                    {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{project?.name || '-'}</td>}
                    <td>{item.clearance_type}</td>
                    <td>{item.authority}</td>
                    <td>{formatDateIN(item.submission_date)}</td>
                    <td>{formatDateIN(item.target_date)}</td>
                    <td><StatusBadge tone={getStatusClass(item.status)}>{item.status}</StatusBadge></td>
                    <td>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(item.id);
                          setNewItem({
                            project_id: String(item.project_id),
                            clearance_type: item.clearance_type || '',
                            authority: item.authority || '',
                            submission_date: item.submission_date || '',
                            target_date: item.target_date || '',
                            status: item.status || 'Submitted',
                            remarks: item.remarks || '',
                            attachments: [],
                          });
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        style={{ marginLeft: 8, color: '#b91c1c', borderColor: '#fecaca' }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!(await confirmDialog('Delete this clearance?', 'Delete Clearance'))) return;
                          try {
                            await clearancesAPI.delete(item.id);
                            setClearances((prev) => prev.filter((c) => c.id !== item.id));
                            notifySuccess('Clearance deleted.');
                          } catch (error) {
                            console.error('Clearance delete failed:', error);
                            notifyError('Failed to delete clearance. Please retry.');
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{selectedItem.clearance_ref} - Detail</h2>
              <button className="btn-close" onClick={() => setSelectedItem(null)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedItem.linked_correspondence} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Type</p><p style={{ fontSize: 14 }}>{selectedItem.clearance_type}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Authority</p><p style={{ fontSize: 14 }}>{selectedItem.authority}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Submission Date</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedItem.submission_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Target Date</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedItem.target_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Status</p><p><StatusBadge tone={getStatusClass(selectedItem.status)}>{selectedItem.status}</StatusBadge></p></div>
              </div>
              {selectedItem.remarks && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Remarks</p>
                  <p style={{ fontSize: 13, color: '#374151' }}>{selectedItem.remarks}</p>
                </div>
              )}
              {Array.isArray(selectedItem.attachments) && selectedItem.attachments.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Attachments</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedItem.attachments.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px' }}>
                        <span style={{ fontSize: 13 }}>{file.name}</span>
                        <span style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-secondary btn-sm" onClick={() => handleViewClearanceAttachment(selectedItem, file)}>View</button>
                          <button className="btn-secondary btn-sm" onClick={() => handleDownloadClearanceAttachment(selectedItem, file)}>Download</button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New Clearance</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Project <span className="req">*</span></label>
                <select value={newItem.project_id} onChange={(e) => setNewItem({ ...newItem, project_id: e.target.value })}>
                  <option value="">Select Project</option>
                  {projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Clearance Type <span className="req">*</span></label>
                  <select value={newItem.clearance_type} onChange={(e) => setNewItem({ ...newItem, clearance_type: e.target.value })}>
                    <option value="">Select Clearance Type</option>
                    {clearanceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Authority <span className="req">*</span></label>
                  <input type="text" value={newItem.authority} onChange={(e) => setNewItem({ ...newItem, authority: e.target.value })} placeholder="Approving authority" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Submission Date <span className="req">*</span></label>
                  <input type="date" value={newItem.submission_date} onChange={(e) => setNewItem({ ...newItem, submission_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Target Date <span className="req">*</span></label>
                  <input type="date" value={newItem.target_date} onChange={(e) => setNewItem({ ...newItem, target_date: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status <span className="req">*</span></label>
                  <select value={newItem.status} onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}>
                    <option value="Draft">Draft</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Under Review">Under Review</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea value={newItem.remarks} onChange={(e) => setNewItem({ ...newItem, remarks: e.target.value })} rows={2} placeholder="Optional remarks..." />
              </div>
              <div className="form-group">
                <label>Attachments (optional)</label>
                <input type="file" multiple onChange={handleAttachmentChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.msg,.eml" disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newItem.attachments.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {newItem.attachments.map((file, idx) => (
                      <div key={`${file.name}-${file.size}-${idx}`} style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{file.name}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#22c55e' }}>✓</span>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveAttachment(idx)} title="Remove">✕</button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {formError && <p style={{ color: '#dc2626', fontSize: 12 }}>{formError}</p>}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSave} disabled={attachmentUploading}>
                  {editingId ? 'Save Clearance' : 'Create Clearance'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clearance;
